import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GlassCard, Icon } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { templateService } from '../../../../services/apiService';
import { TemplateDocument, useTemplateDraft } from './TemplateBuilder';

/**
 * The full-page template editor (user decision, 25 Oct 2026): the document workspace — the
 * scratch pad (or the guided form) as a zoomable paper, the mail exactly as it will be sent
 * beside it. Reached from a row on the Templates screen, or from the New Template dialog once a
 * template is created.
 *
 *   /campaigning-tool/templates/new   — a new template
 *   /campaigning-tool/templates/:id   — an existing one
 */
export function TemplateEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const api = useApiData(() => templateService.get(id), [id], { enabled: !isNew });

  if (!isNew && api.error) {
    return (
      <GlassCard>
        <p className="text-sm text-cos-status-coral">{api.error}</p>
      </GlassCard>
    );
  }
  if (!isNew && !api.data) {
    return (
      <GlassCard>
        <p className="flex items-center gap-2 text-sm text-cos-ink-secondary">
          <Icon name="progress_activity" className="animate-spin text-[18px]" />
          Loading the template…
        </p>
      </GlassCard>
    );
  }
  const template = isNew ? null : api.data?.data ?? null;
  return <TemplateEditorBody key={template?.id ?? 'new'} template={template} />;
}

/**
 * Size the workspace to the rest of the shell's scroll area, so its own canvas scrolls and the
 * status bar sits at the bottom of the window. Measured, not `100vh`: the app is drawn through a
 * CSS zoom (--app-scale), under which a viewport unit is not a screen pixel.
 */
function useFillHeight() {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const main = el?.closest('main');
    if (!el || !main) return undefined;
    const fit = () => {
      const cs = getComputedStyle(main);
      const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      // The distance from the top of main's content box to the workspace, in main's own pixels.
      let zoom = 1;
      for (let n = el; n; n = n.parentElement) {
        const z = parseFloat(getComputedStyle(n).zoom);
        if (z && z !== 1) zoom *= z;
      }
      const offset = (el.getBoundingClientRect().top - main.getBoundingClientRect().top) / zoom + main.scrollTop - parseFloat(cs.paddingTop);
      el.style.height = `${Math.max(520, Math.floor(main.clientHeight - pad - offset))}px`;
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(main);
    window.addEventListener('resize', fit);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, []);
  return ref;
}

function TemplateEditorBody({ template }) {
  const navigate = useNavigate();
  const location = useLocation();
  const handed = location.state?.draft;
  const draft = useTemplateDraft(template, { initialMode: handed?.mode, initialDraft: handed?.d });
  const { isNew, d, mode } = draft;
  const fill = useFillHeight();
  // Full screen: the same workspace over the whole window, the shell hidden. Escape leaves it
  // when nothing else (a popover, a dialog) is open to catch the key first.
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.sp-popover, [role="dialog"]')) return;
      setFull(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [full]);

  usePageChrome(
    () => ({
      title: d.name.trim() || (isNew ? 'New template' : 'Edit template'),
      subtitle: mode === 'scratch' ? 'Templates · scratch pad' : 'Templates · guided form',
    }),
    [d.name, isNew, mode]
  );

  const doc = (
    <TemplateDocument
      draft={draft}
      chrome="page"
      fullscreen={full}
      onToggleFullscreen={() => setFull((f) => !f)}
      onClose={() => navigate('/campaigning-tool/templates')}
      onSaved={(row) => {
        if (isNew && row?.id) navigate(`/campaigning-tool/templates/${row.id}`, { replace: true });
      }}
    />
  );

  return (
    <div ref={fill} className="min-h-[520px]">
      {full ? createPortal(<div className="campaign-os tpl-overlay">{doc}</div>, document.body) : doc}
    </div>
  );
}

export default TemplateEditorPage;
